import type { AnyClock } from "@repo/lib/clock";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import {
  RepoCache,
  type GetObservableOptions,
} from "@repo/shared-client/repoCache";
import { PatchSiteDtoSchema, type Site } from "@repo/types";
import { cacheDefaults } from "./common";
import { apiService } from "@/services/apiService";
import type { Observable } from "rxjs";
import { nullObservable } from "@repo/shared-client/commonObservables";
import type { OnlyRequire } from "@repo/lib/types";
import { RepoMutationHooks } from "./repoMutationHooks";
import { baseRepo } from "./baseRepo";

type SiteGetKey = { type: "get"; tenantId: string };
type SiteKey = SiteGetKey;

export type SiteDto = Site & {
  tenantId: string;
};

export type SiteIdPath = { tenantId: string };

export class SiteRepository {
  private cache: RepoCache<string, SiteDto, ObjectKeyHasher, AnyClock>;
  private hooks = new RepoMutationHooks<SiteIdPath, SiteDto>();

  // expose hook registry
  addHook = this.hooks.addHook;
  removeHook = this.hooks.removeHook;

  publishNewer(site: SiteDto) {
    this.cache.publish(site.tenantId, site);
  }

  siteToDto(tenantId: string, site: Site): SiteDto;
  siteToDto(tenantId: string, site?: null): null;
  siteToDto(tenantId: string, site?: Site | null): SiteDto | null;
  siteToDto(tenantId: string, site?: Site | null): SiteDto | null {
    return site ? { ...site, tenantId } : null;
  }

  constructor() {
    this.cache = new RepoCache({
      requestKeyHasher: new ObjectKeyHasher({ sortKeys: false }),
      expiresAfterSeconds: cacheDefaults.expiresAfterSeconds,
      purgeExpirationAfterSeconds: cacheDefaults.purgeExpiredAfterSeconds,
      fetch: async ({ requestKey, publish }) => {
        const key = requestKey as SiteKey;

        switch (key.type) {
          case "get": {
            const { tenantId } = key;
            const site = (await apiService.v1.getSite(tenantId)).data;
            publish(tenantId, this.siteToDto(tenantId, site));
            break;
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const key = requestKey as SiteKey;

        switch (key.type) {
          case "get": {
            return table.get(key.tenantId) ?? null;
          }
        }
      },
      affectedKeys: ({ entryData, affectsKey }) => {
        // extract
        const { tenantId } = entryData;
        // affect
        affectsKey(this.getKey(tenantId));
      },
    });

    baseRepo.addRepo(this);
  }

  private getKey(tenantId: string): SiteGetKey {
    return { type: "get", tenantId };
  }

  get(
    tenantId: string,
    options?: GetObservableOptions
  ): Observable<SiteDto | null> {
    if (!tenantId) {
      return nullObservable;
    }
    const key = this.getKey(tenantId);
    return this.cache.observable(key, options);
  }
  getCached(tenantId: string): SiteDto | null {
    const key = this.getKey(tenantId);
    return this.cache.collectIfUnexpired(key, null);
  }

  private async updateInternal(
    action: "create" | "update",
    tenantId: string,
    data: Partial<SiteDto>
  ): Promise<SiteDto> {
    const hooks = this.hooks.execute({ tenantId }, action);
    data = await hooks.before(data);

    let updated = this.siteToDto(
      tenantId,
      (await apiService.v1.updateSite(tenantId, PatchSiteDtoSchema.parse(data)))
        .data
    );

    updated = await hooks.after(updated);

    // publish new site
    this.cache.publish(tenantId, updated);

    return updated;
  }

  async create(tenantId: string, data: Partial<SiteDto>): Promise<SiteDto> {
    if (!tenantId) {
      throw new Error("Failed to create site: tenantId is blank.");
    }

    return await this.updateInternal("create", tenantId, data);
  }

  async update(tenantId: string, data: Partial<SiteDto>): Promise<SiteDto> {
    if (!tenantId) {
      throw new Error("Failed to update site: tenantId is blank.");
    }

    return this.updateInternal("update", tenantId, data);
  }

  invalidate(site: OnlyRequire<SiteDto, "id">) {
    this.cache.invalidateEntry(site.id);
  }

  invalidateAll() {
    this.cache.clear();
  }

  init(data?: Partial<SiteDto>): SiteDto {
    return baseRepo.init({
      tenantId: baseRepo.getTenantId() || "UNKNOWN",
      id: "site",
      logo: "",
      banner: "",
      colorScheme: {
        primary: "#80D8FF",
        secondary: "#37474F",
        tertiary: "#FBE9E7",
        error: "#FF5252",
        success: "#4CAF50",
        warning: "#FFC107",
        info: "#2196F3",
      },
      status: "draft",
      ...data,
    });
  }
}

export const siteRepo = new SiteRepository();
